import React from 'react';

/**
 * 📋 REJA — loyiha kanban doskasi (ClickUp uslubida).
 *
 * Reja ≠ vazifa: vazifa berilishi bilan engine ishlay boshlaydi, reja esa kutadi.
 * Shuning uchun kartani vazifaga aylantirish — ALOHIDA, bir bosishli amal (▶).
 *
 * `authFetch` har render'da yangi identity — effekt dep'iga QO'YILMAYDI (refetch-storm
 * tuzog'i), ref orqali ishlatiladi.
 */

// 🔴 Kutilmagan qiymat (masalan boshqa joydan kelgan priority=3) PRIO[x] ni undefined
// qilib butun React daraxtini qulatgan edi (2026-09-05, «Nimadir xato ketdi» ekrani).
// Shuning uchun ro'yxatda yo'q qiymat DOIM «Oddiy» ga tushadi — sahifa hech qachon oq
// bo'lib qolmaydi. Ma'lumot yozuvchi tomon ham tuzatilgan, bu ikkinchi qatlam.
const prio = (v) => PRIO[v] || PRIO[1];
const PRIO = {
  2: { t: 'Yuqori', cls: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400' },
  1: { t: 'Oddiy',  cls: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400' },
  0: { t: 'Past',   cls: 'bg-gray-50 dark:bg-gray-700 text-gray-400' },
};

const TASK_LBL = {
  pending: 'navbatda', pending_architecture: 'rejalashtirilmoqda', in_progress: 'bajarilmoqda',
  awaiting_clarification: 'savol kutmoqda', completed: 'bajarildi', failed: 'xato', cancelled: 'bekor',
};

/**
 * 📝 Karta muharriri — YANGI karta qo'shishda ham, mavjudini TAHRIRLASHDA ham
 * AYNAN shu forma ishlaydi (parallel implementatsiya yozilmasin).
 *
 * 🔴 TOP-LEVEL komponent: TodoPanel ichida e'lon qilinsa har render'da YANGI
 * komponent turi bo'lib, input fokusi va yozilayotgan matn yo'qolardi.
 */
function CardEditor({ apiUrl, uploadFiles, initial = {}, submitLabel, onSave, onCancel }) {
  const [title, setTitle] = React.useState(initial.title || '');
  const [detail, setDetail] = React.useState(initial.detail || '');
  const [images, setImages] = React.useState(initial.images || []);
  const [busy, setBusy] = React.useState(false);

  const add = async (files) => {
    const arr = Array.from(files || []).filter(f => f && /^image\//.test(f.type));
    if (!arr.length) return;
    setBusy(true);
    const got = await uploadFiles(arr.slice(0, 6));
    setBusy(false);
    if (got.length) setImages(p => [...p, ...got].slice(0, 6));
  };
  const save = () => { if (title.trim().length >= 2) onSave({ title: title.trim(), detail: detail.trim() || null, images }); };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-primary-300 dark:border-primary-700 p-2"
         onPaste={e => add(e.clipboardData && e.clipboardData.files)}>
      <input autoFocus value={title} onChange={e => setTitle(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') onCancel(); }}
        placeholder="Nima qilish kerak?"
        className="w-full text-xs border-0 focus:outline-none" />
      <textarea value={detail} onChange={e => setDetail(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) save(); if (e.key === 'Escape') onCancel(); }}
        placeholder="Batafsil (ixtiyoriy) — nima kerakligini yozing"
        rows={2}
        className="w-full text-[11px] text-gray-600 dark:text-gray-400 border-0 focus:outline-none resize-y mt-1 placeholder-gray-300" />

      <div className="flex items-center gap-1 flex-wrap mt-1">
        {images.map(u => (
          <span key={u} className="relative">
            <img src={`${apiUrl}${u}`} alt=""
              onClick={() => window.open(`${apiUrl}${u}`, '_blank')}
              className="w-11 h-11 object-cover rounded border border-gray-200 dark:border-gray-700 cursor-zoom-in" />
            <button onClick={() => setImages(p => p.filter(x => x !== u))} title="Olib tashlash"
              className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-[9px] text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-300 leading-none">✕</button>
          </span>
        ))}
        <label className={`text-[10px] px-2 py-1 rounded border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${busy ? 'opacity-50' : ''}`}>
          {busy ? '⏳ Yuklanmoqda…' : '📎 Rasm'}
          <input type="file" accept="image/*" multiple className="hidden"
            onChange={e => { add(e.target.files); e.target.value = ''; }} />
        </label>
        <span className="text-[9px] text-gray-400">yoki ⌘/Ctrl+V</span>
      </div>

      <div className="flex gap-1 mt-1.5">
        <button onClick={save} disabled={title.trim().length < 2}
          className="text-[10px] px-2 py-1 rounded bg-primary-700 dark:bg-primary-600 text-white font-semibold disabled:opacity-40">{submitLabel}</button>
        <button onClick={onCancel} className="text-[10px] px-2 py-1 rounded border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400">Bekor</button>
      </div>
    </div>
  );
}

export default function TodoPanel({ authFetch, apiUrl, projectId, onConsole = null }) {
  const [board, setBoard] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [wish, setWish] = React.useState('');
  const [planning, setPlanning] = React.useState(false);
  const [adding, setAdding] = React.useState(null);      // columnId
  const [editId, setEditId] = React.useState(null);   // qaysi karta tahrirlanyapti
  const [openId, setOpenId] = React.useState(null);
  const [busy, setBusy] = React.useState('');
  const [dragId, setDragId] = React.useState(null);
  const [toast, setToast] = React.useState('');
  // 🔐 Konsol akkauntlari — karta "konsolda bajarilsin" deb belgilanishi uchun.
  const [consoles, setConsoles] = React.useState([]);
  const afRef = React.useRef(authFetch);
  afRef.current = authFetch;

  const say = (m) => { setToast(m); setTimeout(() => setToast(''), 3500); };

  const load = React.useCallback(async (quiet) => {
    if (!projectId) return;
    if (!quiet) setLoading(true);
    try {
      const res = await afRef.current(`${apiUrl}/todos/${projectId}`);
      const d = await res.json().catch(() => null);
      if (!res.ok) { setErr((d && d.error) || `Yuklanmadi (${res.status})`); return; }
      setErr('');
      if (d && Array.isArray(d.columns)) setBoard(d);
    } catch (e) { setErr('Server javob bermadi'); }
    finally { setLoading(false); }
  }, [apiUrl, projectId]);

  React.useEffect(() => { setBoard(null); load(); }, [projectId, load]);

  // Konsollar bir marta yuklanadi (o'zgarmaydi). Ruxsat bo'lmasa — jim bo'sh.
  React.useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const r = await afRef.current(`${apiUrl}/console/profiles`);
        if (!r.ok) return;
        const d = await r.json().catch(() => ({}));
        if (!stop && Array.isArray(d.profiles)) setConsoles(d.profiles);
      } catch (e) { /* jim */ }
    })();
    return () => { stop = true; };
  }, [apiUrl]);

  const call = async (url, opts, okMsg) => {
    setBusy(url);
    try {
      const res = await afRef.current(url, opts);
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { say('❌ ' + (d.error || res.status)); return null; }
      if (okMsg) say(okMsg);
      await load(true);
      return d;
    } catch (e) { say('❌ Server javob bermadi'); return null; }
    finally { setBusy(''); }
  };

  const doPlan = async () => {
    const w = wish.trim();
    if (w.length < 4) { say('Rejani yozing'); return; }
    setPlanning(true);
    try {
      const res = await afRef.current(`${apiUrl}/todos/${projectId}/plan`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wish: w }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { say('❌ ' + (d.error || res.status)); return; }
      setWish('');
      say(`✅ ${d.operator} ta 👤 sizning qadamingiz + ${d.work ? '1 ta 🤖 ish' : '0 ta 🤖 ish'} qo'shildi`);
      await load(true);
    } catch (e) { say('❌ Server javob bermadi'); }
    finally { setPlanning(false); }
  };

  // 🖼 Rasm(lar)ni yuklab, /uploads yo'llarini qaytaradi (forma ham, karta ham ishlatadi).
  const uploadFiles = async (files) => {
    const out = [];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append('image', f);
        const r = await afRef.current(`${apiUrl}/upload`, { method: 'POST', body: fd });
        if (r.ok) { const d = await r.json().catch(() => null); if (d && d.imageUrl) out.push(d.imageUrl); }
      } catch (e) { /* pastda aytiladi */ }
    }
    if (!out.length) say('❌ Rasm yuklanmadi');
    return out;
  };

  const addCard = async (columnId, { title, detail, images }) => {
    setAdding(null);
    await call(`${apiUrl}/todos/${projectId}/items`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, detail, columnId, imageUrl: images }) });
  };

  const saveCard = async (it, { title, detail, images }) => {
    setEditId(null);
    await call(`${apiUrl}/todos/item/${it.id}`,
      { method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, detail, imageUrl: images }) },
      '✏️ Saqlandi');
  };

  // 🔺 Prioritet. Ilgari uni FAQAT AI rejalashtiruvchi qo'yardi — operator
  // o'zgartira olmasdi. Yuqori (2) karta vazifaga aylanganda navbat BOSHIGA
  // qo'yiladi (ishlayotgan vazifa to'xtatilmaydi, kutayotganlardan oldin turadi).
  // 🖼 RASM — vazifalardagi bilan bir xil konvensiya (JSON massiv yoki bitta yo'l).
  const imgsOf = (it) => {
    if (!it.image_url) return [];
    try { const a = JSON.parse(it.image_url); return Array.isArray(a) ? a : [String(it.image_url)]; }
    catch { return [String(it.image_url)]; }
  };
  const saveImgs = (it, list) =>
    call(`${apiUrl}/todos/item/${it.id}`,
      { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ imageUrl: list }) },
      null);

  // Fayl tanlash HAM, skrinshotni CTRL+V bilan qo'yish ham shu yerdan o'tadi.
  const addImages = async (it, files) => {
    const arr = Array.from(files || []).filter(f => f && /^image\//.test(f.type));
    if (!arr.length) return;
    setBusy(`img-${it.id}`);
    const added = await uploadFiles(arr.slice(0, 6));
    setBusy('');
    if (!added.length) return;
    await saveImgs(it, [...imgsOf(it), ...added].slice(0, 6));
    say(`🖼 ${added.length} ta rasm biriktirildi`);
  };

  const setPrio = (item, p) =>
    call(`${apiUrl}/todos/item/${item.id}`,
      { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ priority: p }) },
      p === 2 ? '🔺 Yuqori — navbat boshiga qo\'yiladi' : null);

  const move = (item, dir) => {
    const cols = board.columns;
    const i = cols.findIndex(c => c.id === item.column_id);
    const t = cols[i + dir];
    if (!t) return;
    moveTo(item, t.id);
  };

  // Ko'chirish natijasini AYTAMIZ: engine vazifasi yaratildimi, konsol ishi
  // boshlandimi — operator "nima bo'ldi?" deb qolmasin.
  const moveTo = async (it, columnId) => {
    const d = await call(`${apiUrl}/todos/item/${it.id}`,
      { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ columnId }) });
    if (!d) return;
    if (d.consoleStarted) say(`🔐 Konsolda boshlandi (${d.consoleStarted.label}) — 🔐 Konsollar bo'limida kuzating`);
    else if (d.taskId) say(`🛠 Vazifa #${d.taskId} yaratildi — ⏳ Jarayonlarda`);
  };

  const drop = (columnId) => {
    if (!dragId) return;
    const it = board.items.find(x => x.id === dragId);
    setDragId(null);
    if (!it || it.column_id === columnId) return;
    moveTo(it, columnId);
  };

  if (!projectId) return <div className="p-6 text-gray-400 text-sm">Avval loyihani tanlang.</div>;

  const cols = (board && board.columns) || [];
  const items = (board && board.items) || [];

  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">📋 Reja</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 max-w-3xl">
            Rejalarni oldindan yozib boring. Karta <b>⚙️ Bajarilmoqda</b> ustuniga ko'chirilganda
            🤖 ish <b>avtomat vazifaga aylanadi</b> — lekin avval 👤 sizning qadamlaringiz tugagan
            bo'lishi shart (bo'lmasa karta joyida qoladi va sabab aytiladi). Kartalar <b>doska
            tartibida, birin-ketin</b> bajariladi. Kartadagi <b>▶</b> tugmasi ham o'sha ishni qiladi.
          </p>
        </div>
        <button onClick={() => load()} disabled={loading}
          className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
          {loading ? '⏳' : '↻ Yangilash'}
        </button>
      </div>

      {/* 🧠 Istak → qadamlar */}
      <div className="mb-4 p-3 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50/40 dark:bg-primary-900/30">
        <div className="text-xs font-semibold text-primary-900 dark:text-primary-200 mb-1.5">🧠 Rejani qadamlarga bo'lib beray</div>
        <div className="flex gap-2 items-start">
          <textarea
            value={wish} onChange={e => setWish(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) doPlan(); }}
            rows={2} disabled={planning}
            placeholder="Masalan: google map ulash kerak"
            className="flex-1 text-sm border border-primary-200 dark:border-primary-800 rounded-lg px-3 py-2 resize-y min-h-[52px] focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:bg-gray-50 dark:disabled:bg-gray-700"
          />
          <button onClick={doPlan} disabled={planning || wish.trim().length < 4}
            className="text-sm px-4 py-2 rounded-lg bg-primary-700 dark:bg-primary-600 text-white font-semibold hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-50 whitespace-nowrap">
            {planning ? '⏳ O\'ylayapti…' : '➕ Qadamlarga bo\'l'}
          </button>
        </div>
        <div className="text-[10px] text-primary-800/70 dark:text-primary-300/70 mt-1">
          Kodni o'qib chiqadi (~1 daqiqa), shuning uchun allaqachon qilingan narsa qadam bo'lib qo'shilmaydi.
          <b>👤 Sizning qadamlaringiz</b> — alohida-alohida karta (kalit olish, akkaunt, qurilmada sinash).
          <b>🤖 Khorezmai ishi</b> — BITTA karta: siz 👤 qadamlarni tugatgach, bir bosishda butun ish boshlanadi.
          ⌘/Ctrl+Enter — boshlash.
        </div>
      </div>

      {err && <div className="mb-3 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">{err}</div>}
      {toast && <div className="mb-3 text-xs text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2">{toast}</div>}

      {board === null && !err && <div className="text-sm text-gray-400 py-8 text-center">Yuklanmoqda…</div>}

      {board && (
        <div className="flex gap-3 overflow-x-auto pb-3 items-start">
          {cols.map((col, ci) => {
            const list = items.filter(i => i.column_id === col.id);
            return (
              <div key={col.id}
                onDragOver={e => { e.preventDefault(); }}
                onDrop={() => drop(col.id)}
                className="w-72 shrink-0 rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 p-2">
                <div className="flex items-center justify-between gap-1 px-1 pb-2">
                  <button
                    onClick={() => {
                      const t = window.prompt('Ustun nomi:', col.title);
                      if (t && t.trim() && t !== col.title)
                        call(`${apiUrl}/todos/column/${col.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: t.trim() }) });
                    }}
                    title="Nomini o'zgartirish"
                    className="text-sm font-bold text-gray-700 dark:text-gray-300 hover:text-primary-800 dark:hover:text-primary-200 truncate text-left">
                    {col.title}
                  </button>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className="text-[10px] text-gray-400">{list.length}</span>
                    <button
                      onClick={() => {
                        if (!window.confirm(`"${col.title}" ustuni o'chirilsinmi? Kartalar birinchi ustunga ko'chadi.`)) return;
                        call(`${apiUrl}/todos/column/${col.id}`, { method: 'DELETE' }, 'Ustun o\'chirildi');
                      }}
                      title="Ustunni o'chirish"
                      className="text-[10px] text-gray-300 hover:text-red-500">🗑</button>
                  </div>
                </div>

                <div className="space-y-2">
                  {list.map(it => (editId === it.id ? (
                    // ✏️ Tahrirlash — matn HAM, rasm HAM shu formada (ilgari faqat
                    // sarlavha, u ham window.prompt orqali edi; detail va rasmni
                    // umuman o'zgartirib bo'lmasdi).
                    <CardEditor key={it.id} apiUrl={apiUrl} uploadFiles={uploadFiles} submitLabel="Saqlash"
                      initial={{ title: it.title, detail: it.detail, images: imgsOf(it) }}
                      onSave={(v) => saveCard(it, v)} onCancel={() => setEditId(null)} />
                  ) : (
                    <div key={it.id}
                      draggable
                      onDragStart={() => setDragId(it.id)}
                      onDragEnd={() => setDragId(null)}
                      className={`group bg-white dark:bg-gray-800 rounded-lg border p-2 cursor-grab active:cursor-grabbing ${
                        // 👤 operator kartasi — mavzuning O'Z rangi (primary) bilan chap chiziq; amber/qizil ramka
                        // Flowbite palitrasiga hamohang emas edi (operator, 2026-09-18).
                        it.owner === 'operator' ? 'border-gray-200 dark:border-gray-700 border-l-4 border-l-primary-600 dark:border-l-primary-500' : 'border-gray-200 dark:border-gray-700'} ${dragId === it.id ? 'opacity-40' : ''}`}>
                      <div className="flex items-start gap-1.5">
                        <span title={it.owner === 'operator' ? 'Bu qadamni SIZ bajarasiz' : 'Buni Khorezmai bajara oladi'}
                          className="text-xs shrink-0">{it.owner === 'operator' ? '👤' : '🤖'}</span>
                        <button onClick={() => setOpenId(openId === it.id ? null : it.id)}
                          className="text-xs text-gray-900 dark:text-white text-left flex-1 leading-snug hover:text-primary-800 dark:hover:text-primary-200">
                          {it.title}
                        </button>
                        {/* # Karta raqami — Boshqaruvchi/suhbat «#123 karta qo'shdim» deydi, doskada ko'rinmasdi (2026-10-27). */}
                        <span translate="no" title="Karta raqami — Boshqaruvchi va suhbatlarda shu raqam bilan aytiladi"
                          className="text-[10px] text-gray-400 dark:text-gray-500 shrink-0 tabular-nums select-all leading-snug">#{it.id}</span>
                      </div>

                      <div className="flex items-center gap-1 flex-wrap mt-1.5">
                        {it.priority !== 1 && (
                          <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${prio(it.priority).cls}`}>{prio(it.priority).t}</span>
                        )}
                        {it.module_name && <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">{it.module_name}</span>}
                        {it.source === 'ai' && <span className="text-[9px] px-1.5 py-0.5 rounded bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-500" title="AI tuzgan qadam">🧠</span>}
                        {it.owner !== 'operator' && !it.task_id && it.waiting_on > 0 && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400 font-semibold max-w-[15rem] truncate"
                            title={'Avval shu 👤 qadam(lar)ni tugating: ' + (it.waiting_titles || (it.waiting_on + ' ta'))
                              + '  —  kartani «✅ Bajarildi» ustuniga ko\'chiring yoki kerak bo\'lmasa o\'chiring.'}>
                            ⏳ Kutilmoqda: {it.waiting_titles || (it.waiting_on + ' ta 👤 qadam')}
                          </span>
                        )}
                        {it.owner !== 'operator' && !it.task_id && it.plan_id && it.waiting_on === 0 && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400 font-semibold"
                            title="👤 qadamlar tugagan — endi boshlash mumkin">
                            ✅ boshlashga tayyor
                          </span>
                        )}
                        {it.console_key && (
                          <span title="Bu ishni Khorezmai KONSOL orqali bajaradi"
                            className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${
                              it.console_run_id && !it.console_finished_at ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-300 animate-pulse'
                              : it.console_ok ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400'
                              : it.console_run_id ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}>
                            🔐 {it.console_run_id && !it.console_finished_at ? 'konsolda ishlayapti'
                                : it.console_ok ? 'konsolda bajarildi'
                                : it.console_run_id ? 'konsolda tugamadi' : it.console_key}
                          </span>
                        )}
                        {it.task_id && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-300 font-semibold">
                            🛠 #{it.task_id}{it.task_status ? ' · ' + (TASK_LBL[it.task_status] || it.task_status) : ''}
                          </span>
                        )}
                      </div>

                      {!!imgsOf(it).length && (
                        <div className="flex items-center gap-1 mt-1.5">
                          {imgsOf(it).slice(0, 4).map(u => (
                            <img key={u} src={`${apiUrl}${u}`} alt=""
                              onClick={e => { e.stopPropagation(); window.open(`${apiUrl}${u}`, '_blank'); }}
                              title="Kattalashtirish"
                              className="w-9 h-9 object-cover rounded border border-gray-200 dark:border-gray-700 hover:ring-2 hover:ring-primary-300" />
                          ))}
                          {imgsOf(it).length > 4 && (
                            <span className="text-[10px] text-gray-400">+{imgsOf(it).length - 4}</span>
                          )}
                        </div>
                      )}

                      {/* ⚡ YOPIQ kartada ham tezkor amallar: ko'chirish, prioritet, tahrir, o'chirish.
                          Ilgari bular faqat karta OCHILGANDA ko'rinardi — har kichik amal uchun
                          uzun kartani ochib-yopish kerak edi (operator so'rovi, 2026-09-18). */}
                      {openId !== it.id && editId !== it.id && (
                        <div className="flex items-center gap-0.5 mt-1.5 opacity-60 group-hover:opacity-100 transition-opacity"
                          onClick={e => e.stopPropagation()}>
                          <button onClick={() => move(it, -1)} disabled={ci === 0} title="Chapdagi ustunga"
                            className="text-[10px] px-1 py-0.5 rounded border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-30">◀</button>
                          <button onClick={() => move(it, 1)} disabled={ci === cols.length - 1} title="O'ngdagi ustunga"
                            className="text-[10px] px-1 py-0.5 rounded border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-30">▶</button>
                          <span className="w-px h-3.5 bg-gray-200 dark:bg-gray-700 mx-0.5" />
                          {[2, 1, 0].map(p => (
                            <button key={p} onClick={() => setPrio(it, p)}
                              title={p === 2 ? 'Prioritet: Yuqori — vazifaga aylanganda navbat BOSHIGA' : p === 1 ? 'Prioritet: Oddiy' : 'Prioritet: Past — shoshilmaydi'}
                              className={`text-[10px] px-1 py-0.5 rounded border leading-none ${
                                it.priority === p
                                  ? (p === 2 ? 'bg-red-100 dark:bg-red-900/50 border-red-300 dark:border-red-700 text-red-700 dark:text-red-400'
                                             : 'bg-gray-200 dark:bg-gray-600 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300')
                                  : 'border-gray-200 dark:border-gray-700 text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                              {p === 2 ? '🔺' : p === 1 ? '•' : '▽'}
                            </button>
                          ))}
                          <span className="w-px h-3.5 bg-gray-200 dark:bg-gray-700 mx-0.5" />
                          <button onClick={() => { setEditId(it.id); setOpenId(it.id); }} title="Matn va rasmlarni tahrirlash"
                            className="text-[10px] px-1 py-0.5 rounded border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700">✏️</button>
                          <button onClick={() => { if (window.confirm('Karta o\'chirilsinmi?')) call(`${apiUrl}/todos/item/${it.id}`, { method: 'DELETE' }, 'O\'chirildi'); }}
                            title="O'chirish"
                            className="text-[10px] px-1 py-0.5 rounded border border-gray-200 dark:border-gray-700 text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/40 hover:text-red-600 dark:hover:text-red-300 ml-auto">🗑</button>
                        </div>
                      )}

                      {openId === it.id && (
                        <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                          {it.detail && <div className="text-[11px] text-gray-600 dark:text-gray-400 whitespace-pre-wrap leading-relaxed mb-2">{it.detail}</div>}
                          <div className="mb-2" onPaste={e => addImages(it, e.clipboardData && e.clipboardData.files)}>
                            <div className="flex items-center gap-1 flex-wrap">
                              <span className="text-[10px] text-gray-400 mr-0.5">🖼 Rasm:</span>
                              {imgsOf(it).map(u => (
                                <span key={u} className="relative group">
                                  <img src={`${apiUrl}${u}`} alt=""
                                    onClick={() => window.open(`${apiUrl}${u}`, '_blank')}
                                    className="w-12 h-12 object-cover rounded border border-gray-200 dark:border-gray-700 cursor-zoom-in" />
                                  <button onClick={() => saveImgs(it, imgsOf(it).filter(x => x !== u))}
                                    title="Olib tashlash"
                                    className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-[9px] text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-300 leading-none">✕</button>
                                </span>
                              ))}
                              <label className={`text-[10px] px-2 py-1 rounded border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${busy === `img-${it.id}` ? 'opacity-50' : ''}`}>
                                {busy === `img-${it.id}` ? '⏳ Yuklanmoqda…' : '📎 Rasm qo\'shish'}
                                <input type="file" accept="image/*" multiple className="hidden"
                                  onChange={e => { addImages(it, e.target.files); e.target.value = ''; }} />
                              </label>
                            </div>
                            <div className="text-[9px] text-gray-400 mt-1">
                              Skrinshotni shu yerga ⌘/Ctrl+V bilan ham qo'yish mumkin. Karta vazifaga aylanganda rasmlar agentga ham beriladi.
                            </div>
                          </div>
                          {!!consoles.length && (
                            <div className="flex items-center gap-1 flex-wrap mb-2">
                              <span className="text-[10px] text-gray-400 mr-0.5">🔐 Konsolda bajarilsin:</span>
                              <select value={it.console_key || ''}
                                onChange={e => call(`${apiUrl}/todos/item/${it.id}`,
                                  { method: 'PUT', headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ consoleKey: e.target.value || null }) },
                                  e.target.value ? "🔐 Karta konsolga bog'landi — «Bajarilmoqda»ga tashlasangiz Khorezmai o'zi bajaradi" : null)}
                                className="text-[10px] border rounded px-1.5 py-1 max-w-[13rem]">
                                <option value="">— yo'q (oddiy ish) —</option>
                                {consoles.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                              </select>
                            </div>
                          )}
                          {it.console_summary && (
                            <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-2 bg-gray-50 dark:bg-gray-700 border rounded px-2 py-1 whitespace-pre-wrap">
                              🔐 {it.console_summary}
                            </div>
                          )}
                          <div className="flex items-center gap-1 flex-wrap mb-2">
                            <span className="text-[10px] text-gray-400 mr-0.5">Prioritet:</span>
                            {[2, 1, 0].map(p => (
                              <button key={p} onClick={() => setPrio(it, p)}
                                title={p === 2 ? 'Yuqori — vazifaga aylanganda navbat BOSHIGA qo\'yiladi'
                                     : p === 1 ? 'Oddiy — doska tartibida navbatga turadi'
                                     : 'Past — shoshilmaydi'}
                                className={`text-[10px] px-2 py-1 rounded border font-semibold ${
                                  it.priority === p
                                    ? (p === 2 ? 'bg-red-100 dark:bg-red-900/50 border-red-300 dark:border-red-700 text-red-700 dark:text-red-400'
                                               : 'bg-gray-200 dark:bg-gray-600 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300')
                                    : 'border-gray-200 dark:border-gray-700 text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                                {p === 2 ? '🔺 Yuqori' : p === 1 ? 'Oddiy' : 'Past'}
                              </button>
                            ))}
                          </div>
                          <div className="flex items-center gap-1 flex-wrap">
                            <button onClick={() => move(it, -1)} disabled={ci === 0}
                              className="text-[10px] px-1.5 py-1 rounded border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-30">◀</button>
                            <button onClick={() => move(it, 1)} disabled={ci === cols.length - 1}
                              className="text-[10px] px-1.5 py-1 rounded border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-30">▶</button>
                            {!it.task_id && it.owner !== 'operator' && (
                              <button
                                onClick={() => {
                                  if (it.waiting_on > 0) { say('⏳ Avval 👤 belgili qadamlaringizni bajaring'); return; }
                                  if (!window.confirm('Bu ish ENGINE VAZIFASIGA aylantirilsinmi? Khorezmai butun ishni boshlaydi.')) return;
                                  call(`${apiUrl}/todos/item/${it.id}/task`, { method: 'POST' }, '🛠 Vazifa yaratildi');
                                }}
                                disabled={busy.includes(`/item/${it.id}/task`) || it.waiting_on > 0}
                                title={it.waiting_on > 0
                                  ? 'Avval shu 👤 qadam(lar)ni tugating: ' + (it.waiting_titles || '') + ' — kartani «✅ Bajarildi» ustuniga ko\'chiring'
                                  : 'Engine vazifasiga aylantirish'}
                                className="text-[10px] px-2 py-1 rounded bg-primary-700 dark:bg-primary-600 text-white font-semibold hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-40">
                                {it.waiting_on > 0 ? '⏳ 👤 kutilmoqda' : '▶ Vazifa qil'}
                              </button>
                            )}
                            {onConsole && it.owner === 'operator' && (
                              <button
                                onClick={() => onConsole(it)}
                                title="Konsolda ochish — siz kirasiz, qolganini Khorezmai bajaradi"
                                className="text-[10px] px-2 py-1 rounded bg-primary-700 dark:bg-primary-600 text-white font-semibold hover:bg-primary-800 dark:hover:bg-primary-700">
                                🔐 Konsolda
                              </button>
                            )}
                            <button
                              onClick={() => { setEditId(it.id); setOpenId(it.id); }}
                              title="Matn va rasmlarni tahrirlash"
                              className="text-[10px] px-1.5 py-1 rounded border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700">✏️</button>
                            <button
                              onClick={() => {
                                call(`${apiUrl}/todos/item/${it.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ owner: it.owner === 'operator' ? 'khorezmai' : 'operator' }) });
                              }}
                              title="Kim bajaradi (👤 siz / 🤖 Khorezmai)"
                              className="text-[10px] px-1.5 py-1 rounded border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700">
                              {it.owner === 'operator' ? '→🤖' : '→👤'}
                            </button>
                            <button
                              onClick={() => { if (window.confirm('Karta o\'chirilsinmi?')) call(`${apiUrl}/todos/item/${it.id}`, { method: 'DELETE' }, 'O\'chirildi'); }}
                              className="text-[10px] px-1.5 py-1 rounded border border-gray-200 dark:border-gray-700 text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/40 hover:text-red-600 dark:hover:text-red-300 ml-auto">🗑</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )))}

                  {adding === col.id ? (
                    <CardEditor apiUrl={apiUrl} uploadFiles={uploadFiles} submitLabel="Qo'shish"
                      onSave={(v) => addCard(col.id, v)} onCancel={() => setAdding(null)} />
                  ) : (
                    <button onClick={() => setAdding(col.id)}
                      className="w-full text-[11px] text-gray-400 hover:text-primary-800 dark:hover:text-primary-200 hover:bg-white dark:hover:bg-gray-700 rounded-lg py-1.5 border border-dashed border-gray-200 dark:border-gray-700">
                      + Karta
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          <button
            onClick={() => {
              const t = window.prompt('Yangi ustun nomi:', '');
              if (t && t.trim()) call(`${apiUrl}/todos/${projectId}/columns`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: t.trim() }) }, 'Ustun qo\'shildi');
            }}
            className="w-40 shrink-0 text-xs text-gray-400 hover:text-primary-800 dark:hover:text-primary-200 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg py-3 hover:bg-gray-50 dark:hover:bg-gray-700">
            + Ustun
          </button>
        </div>
      )}
    </div>
  );
}
